/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1783296000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Mon Jul 06 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "eagle-eye",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Eagle Eye",
          "depth": 0,
          "id": "connect-to-eagle-eye"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Campaigns",
          "depth": 1,
          "id": "campaigns"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      strong: "strong",
      p: "p",
      ul: "ul",
      li: "li",
      code: "code",
      h3: "h3",
      h4: "h4"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#campaigns",
                children: _jsx(_components.strong, {
                  children: "Campaigns"
                })
              })
            }), _jsx(_components.td, {
              children: "Issue coupons for an Eagle Eye campaign to audience members' wallets"
            }), _jsx(_components.td, {
              children: "Add, Remove"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developer.eagleeye.com/eagleeye-developer/reference/createwalletcampaignaccount",
                children: "Create coupon →"
              })
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-eagle-eye",
        children: "Connect to Eagle Eye"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Eagle Eye"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to ", _jsx(_components.strong, {
          children: "Eagle Eye"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Enter the following fields into Hightouch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Client ID"
          }), ": your Eagle Eye API client ID."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Client secret"
          }), ": your Eagle Eye API client secret. Hightouch uses it to sign every API request with Eagle Eye's SHA-256 hash authentication."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Region"
          }), ": the region your Eagle Eye account is hosted in: ", _jsx(_components.code, {
            children: "us"
          }), ", ", _jsx(_components.code, {
            children: "us2"
          }), ", ", _jsx(_components.code, {
            children: "uk"
          }), ", ", _jsx(_components.code, {
            children: "au"
          }), ", or ", _jsx(_components.code, {
            children: "sandbox.uk"
          }), ". Hightouch uses the region to build the API URL, for example ", _jsx(_components.code, {
            children: "https://wallet.us.eagleeye.com"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "To get your client ID and client secret, contact your Eagle Eye account team."
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Eagle Eye destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Eagle Eye destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "campaigns",
        children: "Campaigns"
      }), "\n", _jsx(_components.p, {
        children: "Syncing to Eagle Eye issues coupons for a campaign you select. When a member enters the audience, Hightouch creates a coupon account for the campaign in the member's Eagle Eye wallet. When a member leaves the audience, Hightouch can optionally cancel the coupons that the member hasn't activated yet."
      }), "\n", _jsxs(_components.p, {
        children: ["Select the Eagle Eye campaign to issue coupons for. The dropdown lists campaigns from your Eagle Eye account that are in ", _jsx(_components.code, {
          children: "ACTIVE"
        }), ", ", _jsx(_components.code, {
          children: "PENDING"
        }), ", ", _jsx(_components.code, {
          children: "DRAFT"
        }), ", or ", _jsx(_components.code, {
          children: "READY"
        }), " status. You can't create campaigns from Hightouch, so create the campaign in Eagle Eye first."]
      }), "\n", _jsx(_components.p, {
        children: "Each sync issues coupons for one campaign. To give the same audience multiple offers, create one sync per campaign from the same audience."
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Eagle Eye coupons have a state that controls whether they apply at checkout:\n", _jsx(_components.code, {
            children: "UNLOADED"
          }), " coupons are issued but not yet activated by the member, and\n", _jsx(_components.code, {
            children: "LOADED"
          }), " coupons are activated. The state a coupon starts in is configured on\nthe campaign in Eagle Eye. Hightouch doesn't set or change coupon states."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "configure-record-matching",
        children: "Configure record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["To match rows from your model to wallets in Eagle Eye, select the model column that contains the member's Eagle Eye ", _jsx(_components.strong, {
          children: "Wallet ID"
        }), ". Eagle Eye coupons are issued directly to wallets, so every row must include a wallet ID. Hightouch rejects rows with a missing or empty wallet ID."]
      }), "\n", _jsxs(_components.p, {
        children: ["Refer to the ", _jsx(_components.a, {
          href: "/syncs/record-matching",
          children: "record matching docs"
        }), " for more information."]
      }), "\n", _jsx(_components.h4, {
        id: "configure-delete-behavior",
        children: "Configure delete behavior"
      }), "\n", _jsx(_components.p, {
        children: "When a member leaves the audience, you can choose how Hightouch handles their coupons:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Keep all coupons"
              })
            }), _jsx(_components.td, {
              children: "Leave all of the member's coupons in place. This is the default."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Cancel unloaded coupons"
              })
            }), _jsxs(_components.td, {
              children: ["Cancel the member's unactivated (", _jsx(_components.code, {
                children: "UNLOADED"
              }), ") coupons for this campaign. Coupons the member has activated (", _jsx(_components.code, {
                children: "LOADED"
              }), ") are never canceled."]
            })]
          })]
        })]
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Canceling a coupon is irreversible. If a member re-enters the audience later,\nHightouch issues a new coupon."
        })
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["For Hightouch platform error codes related to Eagle Eye, see ", _jsx(_components.a, {
            href: "/developer-tools/error-codes#other-destinations",
            children: "Error codes:\nOther destinations"
          }), "."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsxs(_components.p, {
        children: ["When Eagle Eye rejects a coupon issuance, Hightouch rejects the row and includes the error code and message returned by Eagle Eye. Refer to ", _jsx(_components.a, {
          href: "https://developer.eagleeye.com/eagleeye-developer/reference/createwalletcampaignaccount",
          children: "Eagle Eye's API documentation"
        }), " for details on specific error codes."]
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
